import i18next from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDomain, MdLogout, MdPostAdd, MdSettings } from "react-icons/md";
import { RiTableLine } from "react-icons/ri";
import { SlGlobe, SlGraph } from "react-icons/sl";
import { useAuth } from "react-oidc-context";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useGetCurrentUserTags } from "../api/hooks/useRiskThemes";
import { useAdmin } from "../context/adminContext";
import { useDesignSystem } from "../context/designSystemContext";
import { useTenant } from "../context/tenantContext";
import { handleChangeLanguage } from "../languages/i18n";
import { canCreateRisk as getCanCreateRisk } from "../utils/canCreateRisk";

export const MenuLateral = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [menuOpened, setMenuOpened] = useState<Boolean>(false);
  const location = useLocation();
  const isAdmin = useAdmin();
  const { hasMultipleTenants, clearTenant } = useTenant();
  const { i18n } = useTranslation();
  const { designSystem } = useDesignSystem();

  const { data: currentUserTags } = useGetCurrentUserTags()

  function clickSideMenu() {
    setMenuOpened(!menuOpened);
  }

  const stopPropagation = (e: any) => {
    e.stopPropagation();
  };

  const toggleLanguage = () => {
    handleChangeLanguage();
  };

  const getBackgroundColor = (path: string): string => {
    return location.pathname === path
      ? "rounded-lg bg-[#FFFFFF]/20"
      : "transparent";
  };

  const descricao = `${
    !menuOpened ? "hidden" : ""
  } whitespace-nowrap font-raleway text-[12px] leading-[16px] transition-all duration-300`;

  const iconSize = "w-5 h-5";
  const menuItemClass =
    "flex items-center gap-3 p-2 w-full text-white rounded-lg transition-colors duration-200 hover:bg-[#000000]/10";

  const getDataUri = (base64: string | undefined, mimeType: string) => {
    if (!base64) return "";

    const cleanedBase64 = base64.replace(/^data:.*;base64,/, "");
    return `data:${mimeType};base64,${cleanedBase64}`;
  };

  const extendedMimeType = "image/svg+xml";
  const extendedSrc = getDataUri(
    designSystem?.logoImageFileExtended as string,
    extendedMimeType
  );
  const logoSrc = getDataUri(
    designSystem?.logoImageFile as string,
    "image/svg+xml"
  );

  const canCreateRisk = getCanCreateRisk({ isAdmin, currentUserTags });
  const handleLogout = async () => {
    const clientId = auth.settings.client_id;
    await auth.removeUser();
    localStorage.clear();
    await auth.signoutRedirect({
      extraQueryParams: {
        client_id: clientId,
        logout_uri: window.location.origin,
      },
    });
  };
  return (
    <div
      className={`${
        menuOpened ? "pointer-events-auto" : "pointer-events-none"
      } w-full h-full bg-transparent z-50`}
      onClick={() => (menuOpened ? clickSideMenu() : null)}
    >
      <div
        className={`${
          menuOpened
            ? "w-64 min-w-64"
            : "pointer-events-auto w-[70px] min-w-[70px]"
        } h-full py-8 px-4 overflow-hidden transition-all duration-300 z-50 relative shadow-lg flex flex-col`}
        style={{
          backgroundColor: designSystem?.cssBasics.secondaryColor,
        }}
        onClick={stopPropagation}
      >
        <div className="flex flex-col h-full">
          <div className="flex gap-2">
            <img
              className="cursor-pointer h-12 w-auto object-contain"
              src={menuOpened ? extendedSrc : logoSrc}
              alt="photo menu side bar"
              onClick={clickSideMenu}
            />
            {/* <img
              className={`${
                menuOpened ? "opacity-100" : "opacity-0"
              } transition-opacity duration-300`}
              src={`data:image/svg+xml;base64,${designSystem?.logoImageFileExtended}`}
              alt="photo menu side bar"
            /> */}
          </div>

          <ul className="text-white pt-7 flex flex-col gap-1">
            <li className={getBackgroundColor("/")}>
              <Link to={"/"} className={menuItemClass}>
                <div className="flex items-center justify-center">
                  <RiTableLine className={iconSize} />
                </div>
                <div className={`${descricao}`}>{i18next.t("risk_map")}</div>
              </Link>
            </li>

            <li className={getBackgroundColor("/indicators")}>
              <Link to={"/indicators"} className={menuItemClass}>
                <div className="flex items-center justify-center">
                  <SlGraph className={iconSize} />
                </div>
                <div className={`${descricao}`}>{i18next.t("indicators")}</div>
              </Link>
            </li>

            {canCreateRisk && (
              <li className={getBackgroundColor("/add_risk")}>
                <Link to={"/add_risk"} className={menuItemClass}>
                  <div className="flex items-center justify-center">
                    <MdPostAdd className={iconSize} />
                  </div>
                  <div className={`${descricao}`}>{i18next.t("add_risk")}</div>
                </Link>
              </li>
            )}

            {isAdmin && (
              <li className={getBackgroundColor("/settings")}>
                <Link to={"/settings"} className={menuItemClass}>
                  <div className="flex items-center justify-center">
                    <MdSettings className={iconSize} />
                  </div>
                  <div className={`${descricao}`}>{i18next.t("settings")}</div>
                </Link>
              </li>
            )}

            {hasMultipleTenants && (
              <li className={getBackgroundColor("/tenant-selection")}>
                <button
                  onClick={() => {
                    clearTenant();
                    navigate("/tenant-selection");
                  }}
                  className={menuItemClass}
                >
                  <div className="flex items-center justify-center">
                    <MdDomain className={iconSize} />
                  </div>
                  <div className={`${descricao}`}>Select Organization</div>
                </button>
              </li>
            )}
          </ul>

          <div className="mt-auto space-y-2">
            <button
              onClick={toggleLanguage}
              className={`${menuItemClass} group`}
            >
              <div className="flex items-center justify-center relative">
                <SlGlobe className={iconSize} />
                <span
                  className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${
                    i18n.language === "ptBr" ? "bg-blue-500" : "bg-green-500"
                  } border-2 border-transparent`}
                ></span>
              </div>
              <div className={`${descricao} flex items-center gap-2`}>
                <span>
                  {i18n.language === "ptBr" ? "Português" : "English"}
                </span>
                <span className="text-xs opacity-70">→</span>
                <span className="opacity-70">
                  {i18n.language === "ptBr" ? "English" : "Português"}
                </span>
              </div>
            </button>

            <button onClick={handleLogout} className={menuItemClass}>
              <div className="flex items-center justify-center">
                <MdLogout className={iconSize} />
              </div>
              <div className={descricao}>Logout</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
